import { Info, Pencil, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useDeleteCarrierService, useToggleCarrierService } from '@/api/domains/carrier-services';
import type { AvailableMethod, Market } from '@/api/types/services';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { resolveLocale } from '@/i18n';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { cn } from '@/lib/utils';

import { CourierTruckIcon, GripDotsIcon, LockerIcon } from './components/MethodIcons';
import { MethodEditor } from './MethodEditor';
import { MethodSummary } from './MethodSummary';

function MethodGlyph({ methodKey }: { methodKey: string }) {
  if (methodKey.startsWith('locker')) {
    return <LockerIcon className="h-[18px] w-6" />;
  }
  return <CourierTruckIcon className="size-6" />;
}

type Props = {
  market: Market;
  method: AvailableMethod;
  /** Priority for a newly configured method (end of the market's configured list). */
  nextDisplayPriority: number;
  /** Drag handle node, present only for sortable (configured) rows. */
  dragHandle?: ReactNode;
  isDragging?: boolean;
};

/**
 * One catalog method inside a market, laid out per Figma: drag tile · 52px icon
 * tile · name + info glyph · controls; an #ededed hairline below; and the gray
 * panel underneath - quick-setup editor while editing, summary table once
 * configured. Unconfigured rows show the outline "Quick Setup" button, configured
 * ones the dashed-trash / solid-pencil icon buttons.
 */
export function MethodRow({ market, method, nextDisplayPriority, dragHandle, isDragging }: Props) {
  const { t } = useTranslation('markets');
  const tCommon = useTranslation('common').t;
  const [editing, setEditing] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const toggle = useToggleCarrierService(market.id);
  const remove = useDeleteCarrierService(market.id);

  const service = method.service;

  // Close the editor when the service identity changes underneath it (e.g. the
  // service was removed by a concurrent refetch) so the form never edits a ghost.
  const serviceId = service?.id ?? null;
  useEffect(() => {
    setEditing(false);
  }, [serviceId]);

  const handleToggle = (is_enabled: boolean) => {
    if (!service) return;
    toggle.mutate(
      { id: service.id, is_enabled },
      { onError: (error) => toastProblem(toApiProblem(error)) },
    );
  };

  const handleRemove = () => {
    if (!service) return;
    remove.mutate(service.id, {
      onSuccess: () => toast.success(t('methods.removed')),
      onError: (error) => toastProblem(toApiProblem(error)),
    });
  };

  return (
    <div className={cn('bg-background transition-shadow', isDragging && 'opacity-60 shadow-lg')}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-[15px]">
          {dragHandle ?? (
            <span
              aria-hidden="true"
              className="flex size-8 shrink-0 items-center justify-center rounded-[8px] border border-[#888] p-0.5"
            >
              <GripDotsIcon className="size-[15px]" />
            </span>
          )}
          <div className="flex min-w-0 items-center gap-[19px]">
            <span
              className={cn(
                'flex size-[52px] shrink-0 items-center justify-center rounded-xl bg-[#f7f7f7] p-0.5',
                service && !service.is_enabled && 'opacity-50',
              )}
            >
              <MethodGlyph methodKey={method.method_key} />
            </span>
            <span
              className={cn(
                'truncate text-base font-medium text-[#171717]',
                service && !service.is_enabled && 'text-muted-foreground',
              )}
            >
              {method.label}
            </span>
            <Info className="size-6 shrink-0 text-muted-foreground" strokeWidth={1.5} />
            <ExcludedCountriesBadge countries={method.excluded_countries} />
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3.5">
          {service ? (
            <>
              <button
                type="button"
                aria-label={t('methods.remove')}
                onClick={() => setConfirmRemove(true)}
                disabled={remove.isPending}
                className="flex size-9 items-center justify-center rounded-md border border-dashed border-black bg-[#f2f2f2] transition-colors hover:bg-muted disabled:opacity-50"
              >
                <Trash2 className="size-5" />
              </button>
              <button
                type="button"
                aria-label={t('methods.edit')}
                aria-expanded={editing}
                onClick={() => setEditing((v) => !v)}
                className="flex size-9 items-center justify-center rounded-md border border-black bg-background transition-colors hover:bg-accent"
              >
                <Pencil className="size-5" />
              </button>
            </>
          ) : (
            <Button
              variant="outline"
              className="border-black text-[#171717]"
              aria-expanded={editing}
              onClick={() => setEditing((v) => !v)}
            >
              {t('methods.quickSetup')}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-3 border-t border-[#ededed]" />

      {editing ? (
        <MethodEditor
          market={market}
          method={method}
          nextDisplayPriority={nextDisplayPriority}
          onClose={() => setEditing(false)}
        />
      ) : service ? (
        <MethodSummary
          service={service}
          requiresPointSelection={method.requires_point_selection}
          onToggle={handleToggle}
          togglePending={toggle.isPending || remove.isPending}
        />
      ) : null}

      <AlertDialog open={confirmRemove} onOpenChange={setConfirmRemove}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('methods.confirmRemove.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('methods.confirmRemove.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemove}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('methods.remove')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

/** Per-country exception badge - shown when a method can't reach part of its market. */
function ExcludedCountriesBadge({ countries }: { countries: string[] }) {
  const { t, i18n } = useTranslation('markets');
  const locale = resolveLocale(i18n.language);

  const names = useMemo(() => {
    if (countries.length === 0) return '';
    let displayNames: Intl.DisplayNames | null = null;
    try {
      displayNames = new Intl.DisplayNames([locale], { type: 'region' });
    } catch {
      displayNames = new Intl.DisplayNames(['en'], { type: 'region' });
    }
    return countries.map((code) => displayNames?.of(code) ?? code).join(', ');
  }, [countries, locale]);

  if (countries.length === 0) return null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            variant="outline"
            className="border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/40 dark:bg-amber-950/40 dark:text-amber-200"
          >
            {t('methods.excludedBadge', { countries: countries.join(', ') })}
          </Badge>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {t('methods.excludedTooltip', { countries: names })}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
